import React, { useState } from 'react';
import { ChevronLeft, ChevronRight, Clock, CheckCircle, Circle, Play } from 'lucide-react';
import { ScriptPhase } from './ScriptPhase';

interface CallScriptViewerProps {
  scriptData: any;
  currentPhase: number;
  onPhaseChange: (phase: number) => void;
  isCallActive: boolean;
}

export const CallScriptViewer: React.FC<CallScriptViewerProps> = ({
  scriptData,
  currentPhase,
  onPhaseChange,
  isCallActive
}) => {
  const [completedPhases, setCompletedPhases] = useState<Set<number>>(new Set());
  const [notes, setNotes] = useState<Record<number, string>>({});

  const phases = scriptData?.script_data?.script_phases || [];
  const totalPhases = phases.length;
  const progressPercentage = ((currentPhase + 1) / totalPhases) * 100;

  const handleNextPhase = () => {
    if (currentPhase < totalPhases - 1) {
      setCompletedPhases(prev => new Set([...prev, currentPhase]));
      onPhaseChange(currentPhase + 1);
    }
  };

  const handlePreviousPhase = () => {
    if (currentPhase > 0) {
      onPhaseChange(currentPhase - 1);
    }
  };

  const handlePhaseClick = (phaseIndex: number) => {
    onPhaseChange(phaseIndex);
  };

  const handleNoteChange = (phaseIndex: number, note: string) => {
    setNotes(prev => ({ ...prev, [phaseIndex]: note }));
  };

  const formatDuration = (seconds: number) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  const getPhaseIcon = (phaseIndex: number) => {
    if (completedPhases.has(phaseIndex)) {
      return <CheckCircle className="w-4 h-4 text-emerald-400" />;
    } else if (phaseIndex === currentPhase) {
      return isCallActive ? <Play className="w-4 h-4 text-cyan-400" /> : <Circle className="w-4 h-4 text-cyan-400" />;
    } else {
      return <Circle className="w-4 h-4 text-slate-500" />;
    }
  };


  return (
    <div className="bg-slate-800/60 backdrop-blur-2xl rounded-2xl border border-slate-700/60 shadow-2xl shadow-black/20 overflow-hidden">
      {/* Header */}
      <div className="px-6 py-5 border-b border-slate-700/60 bg-gradient-to-r from-slate-800/80 to-slate-800/40">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-4">
            <div className="p-3 bg-gradient-to-br from-cyan-500/20 to-blue-600/20 rounded-xl border border-cyan-500/30 shadow-lg shadow-cyan-500/10">
              <Play className="w-5 h-5 text-cyan-400" />
            </div>
            <div>
              <h3 className="text-lg font-bold text-white">Call Script</h3>
              <p className="text-sm text-slate-400 mt-0.5">
                Phase {currentPhase + 1} of {totalPhases}
              </p>
            </div>
          </div>
          <div className="flex items-center gap-1.5">
            <div className="w-2 h-2 bg-cyan-400 rounded-full animate-pulse"></div>
            <div className="w-2 h-2 bg-blue-400 rounded-full animate-pulse" style={{ animationDelay: '150ms' }}></div>
            <div className="w-2 h-2 bg-violet-400 rounded-full animate-pulse" style={{ animationDelay: '300ms' }}></div>
          </div>
        </div>
      </div>

      {/* Progress Bar */}
      <div className="px-6 py-4 border-b border-slate-700/60 bg-slate-900/30">
        <div className="flex items-center justify-between mb-3">
          <span className="text-sm font-medium text-slate-300">
            Progress
          </span>
          <span className="text-sm font-bold text-cyan-400">
            {Math.round(progressPercentage)}% Complete
          </span>
        </div>
        <div className="relative w-full bg-slate-700/60 rounded-full h-3 overflow-hidden">
          <div 
            className="absolute inset-y-0 left-0 bg-gradient-to-r from-cyan-500 via-blue-500 to-violet-500 rounded-full transition-all duration-500 ease-out"
            style={{ width: `${progressPercentage}%` }}
          >
            <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/20 to-transparent animate-shimmer"></div>
          </div>
        </div>
      </div>

      {/* Phase Navigation - Redesigned as tabs */}
      <div className="px-6 py-4 border-b border-slate-700/60 bg-slate-900/20">
        <div className="flex flex-wrap gap-2">
          {phases.map((phase: any, index: number) => {
            const isActive = index === currentPhase;
            const isCompleted = completedPhases.has(index);
            
            return (
              <button
                key={phase.id}
                onClick={() => handlePhaseClick(index)}
                className={`group relative flex items-center gap-2.5 px-4 py-2.5 rounded-xl transition-all duration-200 ${
                  isActive
                    ? 'bg-gradient-to-r from-cyan-500/20 to-blue-600/20 text-cyan-300 border-2 border-cyan-500/50 shadow-lg shadow-cyan-500/10'
                    : isCompleted
                    ? 'bg-emerald-500/15 text-emerald-300 border border-emerald-500/40 hover:bg-emerald-500/25'
                    : 'bg-slate-700/40 text-slate-400 hover:text-slate-200 hover:bg-slate-700/60 border border-slate-600/40'
                }`}
              >
                {getPhaseIcon(index)}
                <span className="text-sm font-medium">{phase.name}</span>
                <div className={`flex items-center gap-1 text-xs px-2 py-0.5 rounded-full ${
                  isActive ? 'bg-cyan-500/20 text-cyan-300' : 
                  isCompleted ? 'bg-emerald-500/20 text-emerald-300' : 
                  'bg-slate-600/40 text-slate-400'
                }`}>
                  <Clock className="w-3 h-3" />
                  <span>{formatDuration(phase.duration)}</span>
                </div>
                {isActive && (
                  <div className="absolute -top-1 -right-1 w-3 h-3 bg-cyan-400 rounded-full animate-ping"></div>
                )}
              </button>
            );
          })}
        </div>
      </div>

      {/* Current Phase Content */}
      <div>
        {phases[currentPhase] && (
          <ScriptPhase
            phase={phases[currentPhase]}
            phaseIndex={currentPhase}
            isActive={isCallActive}
            note={notes[currentPhase] || ''}
            onNoteChange={(note) => handleNoteChange(currentPhase, note)}
          />
        )}
      </div>

      {/* Navigation Controls */}
      <div className="px-6 py-5 border-t border-slate-700/60 bg-slate-900/40">
        <div className="flex items-center justify-between">
          <button
            onClick={handlePreviousPhase}
            disabled={currentPhase === 0}
            className="flex items-center gap-2 px-5 py-2.5 bg-slate-700/50 hover:bg-slate-700/70 disabled:bg-slate-800/30 disabled:text-slate-600 text-slate-200 rounded-xl transition-all duration-200 border border-slate-600/50 disabled:border-slate-700/30"
          >
            <ChevronLeft className="w-4 h-4" />
            <span className="font-medium">Previous</span>
          </button>

          <div className="flex items-center gap-3">
            {phases.map((_: any, index: number) => (
              <button
                key={index}
                onClick={() => handlePhaseClick(index)}
                className={`w-2.5 h-2.5 rounded-full transition-all duration-200 ${
                  index === currentPhase
                    ? 'bg-cyan-400 ring-4 ring-cyan-400/30'
                    : completedPhases.has(index)
                    ? 'bg-emerald-400'
                    : 'bg-slate-600 hover:bg-slate-500'
                }`}
              />
            ))}
          </div>

          <button
            onClick={handleNextPhase}
            disabled={currentPhase >= totalPhases - 1}
            className="flex items-center gap-2 px-5 py-2.5 bg-gradient-to-r from-cyan-500 to-blue-600 hover:from-cyan-400 hover:to-blue-500 disabled:from-slate-700/50 disabled:to-slate-700/50 disabled:text-slate-500 text-white rounded-xl transition-all duration-200 shadow-lg shadow-cyan-500/20 hover:shadow-cyan-500/40 disabled:shadow-none font-medium"
          >
            <span>Next</span>
            <ChevronRight className="w-4 h-4" />
          </button>
        </div>
      </div>
    </div>
  );
};
